/**
 * The REFERENCES thumbnail strip + [+] button.
 *
 * Extracted from `ConfigTabContent` so the Canvas UI 2.0 Refine tab can render
 * the same row rather than growing a second copy of it — the same mistake the
 * Config | Variants footer made three times over (see `QuickInfoBar`).
 *
 * Tabs own selection policy and persistence. This row only emits explicit
 * reference changes; it never edits prompt content.
 */

import { memo } from 'react'
import { X } from 'lucide-react'
import { AddReferenceButton } from './AddReferenceButton'
import { referenceInteractionLog } from '../../CanvasReferencePicking'
import { useTranslation } from 'react-i18next'
import { REFERENCE_SECTION_LABEL_KEY } from './referenceSectionLabel'
import type { SelectedReference } from './ReferenceSelector'
import { ReferenceThumbnailButton } from './ReferenceMediaPreview'

export interface ReferenceRowProps {
  /** Currently attached references. */
  references: SelectedReference[]
  /** Project ID for thumbnail URL construction. */
  projectId: string
  /** Open the reference picker from the explicit [+] button. */
  onOpenSelector: () => void
  /** Owning node ID, used to correlate preview traces. */
  ownerNodeId?: string
  /** Cache-bust key for reference media URLs. */
  cacheBust?: string
  onSelectCanvas?: () => void
  onReferencesChange?: (references: SelectedReference[]) => void
}

const ReferenceRow = memo(function ReferenceRow({
  references,
  projectId,
  onOpenSelector,
  ownerNodeId,
  cacheBust,
  onSelectCanvas,
  onReferencesChange,
}: ReferenceRowProps) {
  const { t } = useTranslation('canvas')
  return (
    <div>
      <label className="text-xxs uppercase tracking-wider text-text-muted block mb-1">
        {t(REFERENCE_SECTION_LABEL_KEY)}
      </label>
      <div
        className="flex items-center gap-2 min-h-[36px]"
        data-testid="reference-row"
      >
        {references.length > 0 && (
          <div
            className="flex gap-1 overflow-x-auto py-1"
            data-testid="reference-thumbnails"
          >
            {references.map((ref, index) => (
              <div
                key={`${ref.mediaType}-${ref.nodeId}-${ref.fromIndex ?? index}`}
                className="relative group/ref shrink-0"
              >
                <ReferenceThumbnailButton
                  panelInteractions
                  audioTestId={`reference-audio-thumbnail-${ref.nodeId}`}
                  reference={ref}
                  projectId={projectId}
                  cacheBust={cacheBust}
                  ownerNodeId={ownerNodeId}
                  surface="reference-row"
                  className="flex-shrink-0 rounded-lg border border-input-border bg-surface-1 hover:bg-surface-3 hover:border-input-border-hover"
                />
                {onReferencesChange && (
                  <button
                    type="button"
                    aria-label={`${t('referenceSelector.remove')}: ${ref.label}`}
                    data-testid={`remove-reference-${ref.nodeId}`}
                    className="absolute right-0 top-0 flex h-4 w-4 items-center justify-center rounded-full border border-input-border bg-surface-0 text-foreground opacity-0 group-hover/ref:opacity-100 focus:opacity-100 focus-visible:ring-2 focus-visible:ring-primary"
                    onClick={() => {
                      onReferencesChange(
                        references.filter(
                          (_, itemIndex) => itemIndex !== index,
                        ),
                      )
                      referenceInteractionLog({
                        event: 'reference:remove',
                        feature: 'canvas-reference-picking',
                        project_id: projectId,
                        owner_node_id: ownerNodeId,
                        reference_node_id: ref.nodeId,
                        reference_count: references.length - 1,
                      })
                    }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <AddReferenceButton
          onOpenSelector={onOpenSelector}
          onSelectCanvas={onSelectCanvas}
        />
      </div>
    </div>
  )
})

ReferenceRow.displayName = 'ReferenceRow'

export { ReferenceRow }
